import keys from 'lodash/keys';
import CustomStore from 'devextreme/data/custom_store';
import { Queries } from './useQueries';
import { QueryListParams, SortDirection } from '#/graphql/types';
import { transformDateTimeValue } from '#/common/utils/datetimeTransform';
import DataStoreOptions from './DataStoreOptions';
import deepMap from '#/common/utils/deepMap';
import { NULL_STR } from '#/config/consts';
import { buildFilters } from '#/common/utils/buildFilters';
import { get, last } from 'lodash';

const transformDataBasedOnColumns = (
  data: any[],
  columns: DataStoreOptions<any>['columns'],
): any[] => {
  if (!columns || !data) return data;

  const dateColumns = columns.filter((col) => col.dataType === 'date').map((col) => col.dataField);

  return data.map((item) => {
    const transformed = { ...item };

    dateColumns.forEach((fieldName) => {
      if (fieldName in transformed) {
        transformed[fieldName] = transformDateTimeValue(transformed[fieldName]);
      }
    });

    dateColumns.forEach((fieldName) => {
      if (fieldName in transformed && transformed[fieldName] === null) {
        transformed[fieldName] = NULL_STR;
      }
    });

    return transformed;
  });
};

/**
 * Transform DevExtreme NOT filters from ['!', ['field', '=', 'value']] to ['field', '<>', 'value']
 * This handles the 'noneof' header filter behavior where DX wraps filters with '!' operator.
 */
const transformNotFilters = (expr: unknown): unknown => {
  if (!Array.isArray(expr)) return expr;

  // Check for NOT pattern: ['!', [field, '=', value]]
  if (
    expr.length === 2 &&
    expr[0] === '!' &&
    Array.isArray(expr[1]) &&
    expr[1].length === 3 &&
    typeof expr[1][0] === 'string' &&
    expr[1][1] === '='
  ) {
    const [field, , value] = expr[1];
    return [field, '<>', value];
  }

  // Recurse into nested arrays
  return expr.map((item) => transformNotFilters(item));
};

/** Normalise any filter by removing stacked outside brackets. */
const removeStackedFilters = (filter: unknown): unknown => {
  if (Array.isArray(filter) && filter.length === 1 && Array.isArray(filter[0])) {
    return removeStackedFilters(filter[0]);
  }
  return filter;
};

/** Interleave conditions with 'AND' to produce a flat filter expression. */
const joinWithAnd = (conditions: unknown[][]): unknown[] =>
  buildFilters(conditions.filter((cond) => cond.length > 0)) ?? [];

const getSortOptions = (sort: unknown): any => (Array.isArray(sort) ? last(sort) : sort);

const getSearchCondition = (
  searchValue: unknown,
  searchKey: DataStoreOptions<any>['searchKey'],
  searchExpr: unknown,
  searchOperation: unknown,
): unknown[][] => {
  if (!searchValue) return [];

  const expr = searchKey || searchExpr;
  if (Array.isArray(expr) && expr.length > 1) {
    const orCondition = expr.flatMap((field, i) =>
      i === 0
        ? [[field, searchOperation, searchValue]]
        : ['OR', [field, searchOperation, searchValue]],
    );
    return [orCondition];
  }

  return [[Array.isArray(expr) ? expr[0] : expr, searchOperation, searchValue]];
};

const getApolloErrorMessage = (response: { error?: { message?: string } }): string =>
  response.error?.message ?? 'Data Loading Error';

/**
 * Unwrap a DevExtreme group key value that may be wrapped in single-element arrays
 * (e.g. [["uuid"]] → "uuid").
 */
const normalizeGroupKeyValue = (value: unknown): unknown => {
  let current = value;
  while (Array.isArray(current) && current.length === 1) {
    current = current[0];
  }
  return current;
};

/**
 * Walk a DevExtreme filter expression tree and return the first equality clause
 * matching `field`. Returns null if not found.
 */
const findEqualityFilterForField = (
  expr: unknown,
  field: string,
): [string, '=', unknown] | null => {
  if (!Array.isArray(expr)) return null;

  if (expr.length === 3 && expr[0] === field && expr[1] === '=') {
    return [field, '=', normalizeGroupKeyValue(expr[2])];
  }

  for (const item of expr) {
    const found = findEqualityFilterForField(item, field);
    if (found) return found;
  }

  return null;
};

/**
 * Build the base GraphQL query params from DevExtreme load options.
 * Extracted to keep the load() function cognitive complexity within the allowed limit.
 */
function buildQueryParams(
  normalizedFilter: unknown,
  sort: { field: string | undefined; descending: boolean },
  pagination: { skip?: number | null; take?: number | null; requireTotalCount?: boolean | null },
  groupSelector: string | null,
  accessTo?: DataStoreOptions<any>['accessTo'],
): QueryListParams {
  return {
    filter: normalizedFilter ? JSON.stringify(normalizedFilter) : null,
    sortBy: sort.field
      ? { field: sort.field, direction: sort.descending ? SortDirection.DESC : SortDirection.ASC }
      : null,
    skip: pagination.skip ?? null,
    take: pagination.take ?? null,
    ...(groupSelector ? { group: groupSelector } : {}),
    requireTotalCount: pagination.requireTotalCount ?? null,
    ...(accessTo ? { accessTo } : {}),
  };
}

/**
 * Phase-1 group rows arrive with `key` (the parentId) but no `id`.
 * CustomStore uses `key: 'id'` for row identity, so without a stable `id`
 * DevExtreme cannot distinguish group rows and mis-attaches child rows under
 * the wrong parent when multiple groups are expanded simultaneously.
 * Prefix with 'group:' to guarantee no collision with real WorkOrder UUIDs
 * (parent WO ids are also valid UUIDs, so id = key alone would collide).
 */
const prefixGroupRowIds = (rows: any[]): any[] =>
  rows.map((row) => (row.key != null && row.id == null ? { ...row, id: `group:${row.key}` } : row));

/**
 * Extract the group key from a Phase-2 loadOptions.filter for the given field.
 * Returns null when the filter does not contain a matching equality clause.
 */
function extractPhase2GroupKey(filter: unknown, groupField: string): string | null {
  const keyClause = findEqualityFilterForField(filter, groupField);
  return keyClause ? String(normalizeGroupKeyValue(keyClause[2])) : null;
}

/**
 * Build a new Map<groupKey, children[]> from a Phase-1 group-row array.
 * Only rows that carry a non-empty items[] are included.
 */
function buildGroupChildrenCache(rows: any[]): Map<string, unknown[]> {
  const cache = new Map<string, unknown[]>();
  for (const row of rows) {
    if (row.key != null && Array.isArray(row.items) && row.items.length > 0) {
      cache.set(String(row.key), row.items as unknown[]);
    }
  }
  return cache;
}

/**
 * Handle the Phase-2 short-circuit for groupKeyOnly mode.
 * Returns a result object if Phase-2 should be served from cache (or empty fallback),
 * or `null` if the call should proceed normally (not a groupKeyOnly Phase-2).
 */
function resolveGroupKeyOnlyPhase2(
  isPhase2: boolean,
  groupExpandFilterMode: string,
  loadOptions: { filter?: unknown; group?: unknown },
  groupChildrenCache: Map<string, unknown[]>,
): { data: unknown[]; totalCount: number; groupCount: null } | null {
  if (!isPhase2 || groupExpandFilterMode !== 'groupKeyOnly') return null;
  const groupField = (get(loadOptions, 'group[0].selector') as string | undefined) ?? 'parentId';
  const groupKey = extractPhase2GroupKey(loadOptions.filter, groupField);
  if (groupKey !== null && groupChildrenCache.has(groupKey)) {
    const cached = groupChildrenCache.get(groupKey) as unknown[];
    return { data: cached, totalCount: cached.length, groupCount: null };
  }
  return { data: [], totalCount: 0, groupCount: null };
}

const createDataStore = <T>(queries: Queries<T>, options: DataStoreOptions<T>) => {
  const { columns, cacheEnabled, key = 'id', searchKey, onGroupCountChange } = options;
  const { getItem, getItemList, getGroupedItemList } = queries;

  let cache: T[] = [];
  let groupChildrenCache = new Map<string, unknown[]>();

  return new CustomStore<T, string>({
    key,
    byKey: async (id) => {
      if (!id) {
        return null as T;
      }
      try {
        if (cacheEnabled && cache.length > 0) {
          const cachedItem = cache.find((item) => item[id as keyof T] === id);
          if (cachedItem) {
            return cachedItem;
          }
        }
        const response = await getItem(id);
        const queryName = keys(response.data)[0];
        const result = response.data[queryName];

        if (result.status === 404) {
          return null as T;
        }

        if (result.status !== 200) {
          throw new Error(getApolloErrorMessage(response));
        }

        return result.data as T;
      } catch (err) {
        console.error(err);
        throw new Error('Data Loading Error');
      }
    },
    load: async (loadOptions) => {
      try {
        const sortOptions = getSortOptions(loadOptions.sort);

        const rawFilter = deepMap<string | null>(loadOptions.filter ?? [], (value) =>
          value === NULL_STR ? null : value,
        );

        const searchCondition = getSearchCondition(
          loadOptions.searchValue,
          searchKey,
          loadOptions.searchExpr,
          loadOptions.searchOperation,
        );

        const filter = removeStackedFilters(
          joinWithAnd([[...rawFilter], ...searchCondition, [...(options.filter ?? [])]]),
        );

        const sortField =
          typeof sortOptions?.selector === 'string'
            ? sortOptions.selector
            : sortOptions?.selector?.context?.column?.dataField;

        // Transform NOT filters ['!', ['field', '=', 'value']] to ['field', '<>', 'value']
        const normalizedFilter = transformNotFilters(filter);

        const params = buildQueryParams(
          normalizedFilter,
          { field: sortField, descending: sortOptions?.desc ?? false },
          {
            skip: loadOptions.skip,
            take: loadOptions.take,
            requireTotalCount: loadOptions.requireTotalCount,
          },
          get(loadOptions, `group[0].selector`, null) as string | null,
          options.accessTo,
        );
        if (options.extraQueryParams) Object.assign(params, options.extraQueryParams);

        const hasGroupSelector = !!get(loadOptions, `group[0].selector`, null);
        const isGroupExpanded = !!get(loadOptions, `group[0].isExpanded`, false);
        const isPhase2 = hasGroupSelector && isGroupExpanded;
        const isPhase1 = hasGroupSelector && !isGroupExpanded;
        const groupExpandFilterMode = options.groupExpandFilterMode ?? 'default';
        const liveExpandedParentIds =
          options.getExpandedParentIds?.() ?? options.expandedParentIds ?? [];

        // Short-circuit DevExtreme's Phase-2 child-list call when groupKeyOnly mode is active.
        // Phase-1's items[] (populated by backend for expandedParentIds) drives rendering.
        // Return the cached children from the last Phase-1 so DX does not overwrite
        // the correct items[] with an empty result after refresh().
        const phase2Result = resolveGroupKeyOnlyPhase2(
          isPhase2,
          groupExpandFilterMode,
          loadOptions,
          groupChildrenCache,
        );
        if (phase2Result) {
          return phase2Result;
        }

        // Read expandedParentIds dynamically via getter (if provided) so the store does not
        // need to be recreated when the user expands/collapses groups.
        // (liveExpandedParentIds already computed above for the load log)

        // Include expandedParentIds in Phase-1 params so the backend populates items[].
        if (isPhase1 && liveExpandedParentIds.length > 0) {
          (params as any).expandedParentIds = liveExpandedParentIds;
        }

        const response = isPhase1 ? await getGroupedItemList(params) : await getItemList(params);
        const queryName = keys(response.data)[0];
        const result = response.data[queryName];

        if (result.status !== 200) {
          throw new Error(getApolloErrorMessage(response));
        }

        // Temporary client-side datetime normalization until backend dates are standardized.
        let transformedData = transformDataBasedOnColumns(result.data ?? [], columns);

        if (isPhase1) {
          transformedData = prefixGroupRowIds(transformedData);
          groupChildrenCache = buildGroupChildrenCache(transformedData);
          // DIAG: log Phase-1 response
          // console.log('[DS] load response grouped', {
          //   loadId,
          //   responseType: 'grouped',
          //   groupCount: transformedData.length,
          //   groups: transformedData.map((g) => ({
          //     key: g.key,
          //     count: g.count,
          //     itemsLength: Array.isArray(g.items) ? g.items.length : null,
          //     summary0: Array.isArray(g.summary) ? g.summary[0] : undefined,
          //   })),
          //   cacheKeys: [...groupChildrenCache.keys()],
          // });
        }

        if (cacheEnabled) {
          cache = transformedData;
        }

        const groupCount = get(result, 'groupCount', null);

        // Phase-1 group response: propagate real group count immediately so
        // the tab badge uses the backend total, not instance.totalCount() (visible rows).
        if (groupCount != null && groupCount > 0) {
          onGroupCountChange?.(groupCount);
        }

        return {
          data: transformedData,
          totalCount: result.totalCount,
          groupCount,
        };
      } catch (err) {
        console.error(err);
        throw new Error('Data Loading Error');
      }
    },
  });
};

export default createDataStore;
